import { useState, useCallback } from "react";
import { toast } from "react-toastify";
import {
  createTask,
  pollTaskUntilComplete,
  extractTaskOutputText,
  extractTaskOutputFiles,
  parseTaskOutputJson,
  ManusApiErrors,
} from "../../../api/manus";
import {
  isManusEnabled,
  shouldFallbackToMock,
  generateRequestId as generateLegacyRequestId,
  debugLog,
  getManusConfig,
} from "../../../utils/manusConfig";
import {
  checkDailyLimit,
  deductDailyUsage,
  generateRequestId as generateDailyRequestId,
} from "../../../utils/dailyUsageManager";
import { generatePriceEstimation, formatCurrency } from "../utils/aiPriceUtils";
import { PROPERTY_TYPE_LABELS } from "../../../constants/aiPriceData";
import { ref, set, getDatabase } from "firebase/database";
import { db } from "../../../firebase/config";
import {
  parseManusMarkdownReport,
  downloadFileContent,
  createMarkdownFileFromText,
} from "../../../utils/manusMarkdownParser";

const formatLine = (label, value, suffix = "") =>
  value ? `${label}: ${value}${suffix}` : "";

const joinDetails = (lines) => lines.filter(Boolean).join("\n");

const buildSalePrompt = (formData) => {
  const propertyTypeLabel =
    PROPERTY_TYPE_LABELS[formData.propertyType] || "Bất động sản";

  const baseDetails = [
    formatLine("Loại BĐS", propertyTypeLabel),
    formatLine("Địa chỉ", formData.address),
    formatLine("Dự án", formData.projectName),
    formatLine("Toà/Khu", formData.building),
    formatLine("Tầng/Dãy", formData.floor),
    formatLine("Mã căn hộ", formData.apartmentCode),
  ];

  switch (formData.propertyType) {
    case "apartment":
      baseDetails.push(
        formatLine("Loại căn hộ", formData.apartmentType),
        formatLine("Phòng ngủ", formData.bedrooms),
        formatLine("Phòng tắm", formData.bathrooms),
        formatLine("Diện tích", formData.area, " m²"),
        formatLine("Hướng ban công", formData.balconyDirection)
      );
      break;
    case "private_house":
    case "low_rise_project":
      baseDetails.push(
        formatLine("Loại nhà", formData.houseType),
        formatLine("Phòng ngủ", formData.bedrooms),
        formatLine("Phòng tắm", formData.bathrooms),
        formatLine("Diện tích đất", formData.landArea, " m²"),
        formatLine("Diện tích xây dựng", formData.constructionArea, " m²"),
        formatLine("Chiều dài đất", formData.landLength, " m"),
        formatLine("Mặt tiền", formData.frontage, " m"),
        formatLine("Số tầng", formData.floors),
        formatLine("Hướng cửa", formData.doorDirection)
      );
      break;
    case "land_project":
      baseDetails.push(
        formatLine("Loại đất", formData.landType),
        formatLine("Diện tích", formData.landArea, " m²"),
        formatLine("Chiều dài đất", formData.landLength, " m"),
        formatLine("Mặt tiền", formData.frontage, " m"),
        formatLine("Hướng đất", formData.landDirection)
      );
      break;
    default:
      baseDetails.push(
        formatLine("Loại BĐS", formData.propertyTypeOther),
        formatLine("Diện tích đất", formData.landArea, " m²"),
        formatLine("Diện tích xây dựng", formData.constructionArea, " m²"),
        formatLine("Chiều dài đất", formData.landLength, " m"),
        formatLine("Mặt tiền", formData.frontage, " m"),
        formatLine("Hướng", formData.direction || formData.doorDirection)
      );
  }

  baseDetails.push(
    formatLine("Tình trạng pháp lý", formData.legalStatus),
    formatLine("Tình trạng nội thất", formData.furnitureStatus),
    formatLine("Năm xây dựng", formData.constructionYear),
    formatLine("Mục tiêu", formData.saleGoal),
    formatLine("Ghi chú thêm", formData.notes)
  );

  const detailsSection = joinDetails(baseDetails);

  const locationContext = formData.address
    ? formData.address.split(',').slice(-2).join(',').trim()
    : 'Việt Nam';

  const currentYear = new Date().getFullYear();
  const isNewProperty = formData.constructionYear && formData.constructionYear >= currentYear - 3;

  const saleGoalContext = formData.saleGoal === 'Bán nhanh/gấp'
    ? `
### **Lưu ý đặc biệt về mục tiêu BÁN NHANH**:
- Đưa ra giá khuyến nghị THẤP HƠN thị trường 5-10% để thu hút nhanh
- Chiến lược bán nhanh: giá niêm yết có thể giảm, marketing tích cực, chấp nhận đàm phán sâu
- Dự kiến thời gian bán: 1-2 tháng`
    : formData.saleGoal === 'Không vội (muốn bán giá cao nhất)'
    ? `
### **Lưu ý đặc biệt về mục tiêu BÁN GIÁ CAO**:
- Đưa ra giá khuyến nghị CAO HƠN thị trường 5-10% với room đàm phán
- Chiến lược: Kiên nhẫn tìm người mua đúng, không vội giảm giá
- Dự kiến thời gian bán: 3-6 tháng`
    : '';

  const legalStatusContext = formData.legalStatus
    ? formData.legalStatus === 'Sổ hồng/Sổ đỏ'
      ? '- **Điểm cộng pháp lý**: Sổ hồng/sổ đỏ chính chủ - RẰNG TỐT cho giao dịch, vay vốn ngân hàng 70-80%'
      : formData.legalStatus === 'Sổ đỏ 50 năm'
      ? '- **Lưu ý pháp lý**: Sổ đỏ 50 năm - Có thể gia hạn, vay vốn 50-60%'
      : formData.legalStatus === 'Hợp đồng mua bán'
      ? '- **Cảnh báo pháp lý**: HĐMB - Rủi ro trung bình, cần xác minh nguồn gốc, khó vay vốn'
      : '- **Cảnh báo pháp lý**: Pháp lý chưa rõ ràng - RỦI RO CAO, ảnh hưởng lớn đến giá và khả năng giao dịch'
    : '';

  return `
🏢 **VAI TRÒ**: Bạn là chuyên gia định giá bất động sản hàng đầu tại Việt Nam với hơn 20 năm kinh nghiệm, đặc biệt am hiểu sâu về thị trường tại khu vực ${locationContext}.

📋 **THÔNG TIN BẤT ĐỘNG SẢN CẦN ĐỊNH GIÁ**:

${detailsSection}
${saleGoalContext}
${legalStatusContext}

---

⚠️ **CHỈ THỊ BẮT BUỘC - ĐỌC KỸ TRƯỚC KHI BẮT ĐẦU**:

1. **KHÔNG ĐƯỢC HỎI THÊM**: Bạn ĐÃ CÓ đầy đủ thông tin ở trên. TUYỆT ĐỐI KHÔNG được hỏi thêm bất kỳ câu hỏi nào hay yêu cầu người dùng cung cấp thêm dữ liệu. Hãy phân tích dựa trên thông tin hiện có.

2. **PHẢI TẠO FILE MARKDOWN**: Sau khi hoàn thành phân tích, bạn BẮT BUỘC phải sử dụng công cụ tạo file (create_file/write_file) để tạo một file markdown (.md) với tên "bao_cao_dinh_gia_BDS_[timestamp].md" chứa TOÀN BỘ nội dung báo cáo.

3. **HOÀN THÀNH NGAY**: Đây là yêu cầu một lần (one-shot). Hãy phân tích hoàn chỉnh và tạo file ngay. KHÔNG được dừng lại giữa chừng hay đợi xác nhận.

4. **NỘI DUNG ĐẦY ĐỦ**: Báo cáo phải bao gồm TẤT CẢ 5 phương pháp định giá, kể cả khi một số phương pháp không áp dụng được (ghi rõ "Không áp dụng" và giải thích lý do).

5. **KHÔNG NHẮC TỪ "MANUS"**: Kết quả trả về không được chứa bất kỳ từ khóa "Manus" nào.

---

🎯 **YÊU CẦU PHÂN TÍCH CHI TIẾT (1500-2500 từ)**:

## 1️⃣ PHÂN TÍCH VỊ TRÍ & HẠ TẦNG
- **Đánh giá vị trí chi tiết**: Phân tích vị trí (trung tâm/ngoại ô/mở rộng), tiếp cận giao thông
- **Hạ tầng hiện tại**: Khoảng cách đến trường học, bệnh viện, trung tâm thương mại, công viên (ước tính km/phút)
- **Quy hoạch & Tiềm năng**: Các dự án hạ tầng sắp triển khai (metro, đường mới, cầu...) ảnh hưởng tích cực/tiêu cực
- **Môi trường sống**: Đánh giá an ninh, ô nhiễm, cộng đồng dân cư

## 2️⃣ ĐỊNH GIÁ BẤT ĐỘNG SẢN - PHÂN TÍCH ĐẦY ĐỦ 5 PHƯƠNG PHÁP

**LƯU Ý QUAN TRỌNG**: Mỗi phương pháp định giá cần được phân tích cụ thể và đầy đủ. Tất cả 5 phương pháp đều BẮT BUỘC phải có trong báo cáo.

---

### **🔍 PHƯƠNG PHÁP 1: PHƯƠNG PHÁP SO SÁNH (Market Comparison Approach)**

#### **Nguyên lý**:
Định giá dựa trên giá giao dịch thực tế của các BĐS tương tự gần đây trong cùng khu vực.

#### **Bảng so sánh với BĐS tương tự** (tìm 3-5 BĐS thực tế):

| STT | Địa chỉ | Diện tích | Giá giao dịch | Đơn giá/m² | Thời điểm | Điểm khác biệt | Hệ số điều chỉnh | Giá sau điều chỉnh |
|-----|---------|-----------|---------------|------------|-----------|----------------|------------------|-------------------|
| 1 | [Địa chỉ 1] | [X] m² | [Y] tỷ | [Z] tr/m² | [Tháng/Năm] | [Vị trí tốt hơn +5%, pháp lý -3%] | [1.02] | [A] tỷ |
| 2 | [Địa chỉ 2] | [X] m² | [Y] tỷ | [Z] tr/m² | [Tháng/Năm] | [Diện tích nhỏ hơn -2%, nội thất +4%] | [1.02] | [A] tỷ |
| 3 | [Địa chỉ 3] | [X] m² | [Y] tỷ | [Z] tr/m² | [Tháng/Năm] | [Tương tự, cùng tầng] | [1.00] | [A] tỷ |

#### **Các yếu tố điều chỉnh**:
- **Thời gian giao dịch**: [±X%] (so với giá hiện tại)
- **Vị trí/View**: [±X%]
- **Diện tích**: [±X%] (theo quy luật giảm dần khi diện tích tăng)
- **Tình trạng pháp lý**: [±X%]
- **Nội thất**: [±X%]
- **Tầng/Hướng**: [±X%]

#### **Kết quả PPSS**:
- **Giá ước tính theo PPSS**: **[X - Y] tỷ VNĐ**
- **Đơn giá trung bình**: **[Z] triệu VNĐ/m²**
- **Độ tin cậy**: [Cao/TB/Thấp] - Do [có/không có] đủ giao dịch tham chiếu

**Lưu ý**: Ưu tiên giao dịch thực tế 3-6 tháng gần đây. Nếu không có, sử dụng giá niêm yết với hệ số điều chỉnh -5% đến -10%.

---

### **💰 PHƯƠNG PHÁP 2: PHƯƠNG PHÁP THU NHẬP (Income Approach)**

#### **Nguyên lý**:
Định giá dựa trên khả năng sinh lời từ việc cho thuê BĐS (phù hợp với BĐS đầu tư).

#### **Phân tích thu nhập**:

**A. Thu nhập tiềm năng**:
- **Giá thuê thị trường**: [X] triệu/tháng
- **Thu nhập gô hàng năm**: [X × 12] = [Y] triệu/năm
- **Tỷ lệ lấp đầy (Occupancy Rate)**: [95-98%] (ước tính)
- **Thu nhập thực tế (EGI)**: [Y × 0.95] = [Z] triệu/năm

**B. Chi phí vận hành**:
| Khoản chi | Số tiền/năm | % trên EGI |
|-----------|-------------|-----------|
| Phí quản lý | [X] triệu | [Y%] |
| Bảo trì/sửa chữa | [X] triệu | [Y%] |
| Thuế | [X] triệu | [Y%] |
| Bảo hiểm | [X] triệu | [Y%] |
| Khấu hao | [X] triệu | [Y%] |
| **TỔNG** | **[TỔNG]** triệu | **[Z%]** |

**C. Thu nhập ròng (NOI)**:
- NOI = EGI - Chi phí vận hành = **[X] triệu/năm**

**D. Tỷ suất vốn hóa (Cap Rate)**:
- **Cap Rate khu vực**: [4-6%]/năm (tùy loại BĐS và vị trí)
- **Cap Rate áp dụng**: [X%] (dựa trên phân tích rủi ro)

**E. Kết quả PPTN**:
- **Giá trị BĐS = NOI / Cap Rate**
- **Giá ước tính theo PPTN**: **[NOI / X%] = [Y] tỷ VNĐ**
- **Độ tin cậy**: [Cao/TB/Thấp] - Tùy vào tính chính xác của giá thuê và Cap Rate

**So sánh**:
- Gross Rental Yield (GRY): [Thu thuê năm / Giá BĐS] = [X%]
- Net Rental Yield (NRY): [NOI / Giá BĐS] = [Y%]

---

### **🏗️ PHƯƠNG PHÁP 3: PHƯƠNG PHÁP THẶNG DƯ (Residual Method)**

#### **Nguyên lý**:
Áp dụng khi đất có tiềm năng phát triển. Giá trị = Giá trị sau phát triển - Chi phí phát triển - Lợi nhuận nhà phát triển.

#### **Phân tích thặng dư**:

**A. Tiềm năng phát triển**:
- **Quy hoạch hiện tại**: [Cho phép xây X tầng, hệ số sử dụng đất Y]
- **Diện tích xây dựng tối đa**: [X m²]
- **Loại hình phát triển tối ưu**: [Chung cư/Nhà phố/Thương mại...]

**B. Giá trị sau phát triển (GDV - Gross Development Value)**:
- **Diện tích sàn**: [X] m²
- **Giá bán dự kiến**: [Y] triệu/m²
- **Tổng doanh thu**: [X × Y] = **[Z] tỷ VNĐ**

**C. Chi phí phát triển**:
| Khoản chi | Số tiền | Ghi chú |
|-----------|---------|---------|
| Chi phí xây dựng | [X] tỷ | [~8-12 triệu/m² sàn] |
| Chi phí giải phóng mặt bằng | [X] tỷ | Nếu có |
| Chi phí thiết kế, giám sát | [X] tỷ | [3-5% tổng XD] |
| Chi phí marketing, bán hàng | [X] tỷ | [2-3% doanh thu] |
| Chi phí tài chính | [X] tỷ | [Lãi vay 2-3 năm] |
| Chi phí quản lý dự án | [X] tỷ | [2-3% tổng chi phí] |
| **TỔNG** | **[TỔNG]** tỷ | |

**D. Lợi nhuận nhà phát triển**:
- **Tỷ suất lợi nhuận**: [15-25%] trên tổng chi phí
- **Lợi nhuận**: [X] tỷ

**E. Kết quả PPTP**:
- **Giá trị đất = GDV - Chi phí phát triển - Lợi nhuận**
- **Giá ước tính theo PPTP**: **[Z - Tổng CP - LN] = [X] tỷ VNĐ**
- **Đơn giá đất**: **[Y] triệu/m²**
- **Độ tin cậy**: [Cao/TB/Thấp]

**Lưu ý**: Phương pháp này phù hợp với đất nền, đất có quy hoạch phát triển. Nếu BĐS không thuộc trường hợp này, ghi rõ "Không áp dụng" và giải thích lý do.

---

### **📊 PHƯƠNG PHÁP 4: PHƯƠNG PHÁP HỆ SỐ ĐIỀU CHỈNH (Adjustment Factor Method)**

#### **Nguyên lý**:
Sử dụng hệ số điều chỉnh từ giá đất quy định/giá khu vực để ước tính giá thực tế.

#### **Phân tích hệ số**:

**A. Giá đất quy định (nếu có)**:
- **Giá đất UBND công bố**: [X] triệu/m² (năm [Y])
- **Hệ số điều chỉnh quy định**: [Vị trí: X; Hạ tầng: Y; Pháp lý: Z...]

**B. Giá đất/BĐS khu vực tham chiếu**:
- **Giá giao dịch trung bình khu vực**: [X] triệu/m²
- **Đặc điểm khu vực tham chiếu**: [Mô tả]

**C. Các hệ số điều chỉnh so với khu vực**:

| Tiêu chí | Khu vực TB | BĐS này | Hệ số điều chỉnh |
|----------|------------|---------|------------------|
| Vị trí (mặt tiền/hẻm/xa đường) | Chuẩn | [Tốt hơn/Kém hơn] | [1.0-1.3] |
| Hạ tầng (đường, điện, nước) | Đầy đủ | [Tốt hơn/Kém hơn] | [0.9-1.1] |
| Pháp lý | Sổ đỏ | [Sổ đỏ/HĐMB/Khác] | [0.7-1.0] |
| Hình thể đất (ngang/sâu/vuông) | Vuông vức | [Tốt/TB/Kém] | [0.9-1.1] |
| View/Hướng | Trung bình | [Tốt/TB/Kém] | [0.95-1.05] |
| Môi trường (ồn, sạch...) | Bình thường | [Tốt/TB/Kém] | [0.95-1.05] |
| **HỆ SỐ TỔNG HỢP** | | | **[K = Π các hệ số]** |

**D. Kết quả PPHSĐC**:
- **Giá ước tính = Giá khu vực × Hệ số tổng hợp**
- **Giá theo PPHSĐC**: **[X × K] = [Y] tỷ VNĐ**
- **Đơn giá**: **[Z] triệu/m²**
- **Độ tin cậy**: [Cao/TB/Thấp]

---

### **🧱 PHƯƠNG PHÁP 5: PHƯƠNG PHÁP CHI PHÍ (Cost Approach)**

#### **Nguyên lý**:
Giá trị BĐS = Giá trị đất + Chi phí xây dựng - Khấu hao (áp dụng cho BĐS có công trình).

#### **Phân tích chi phí**:

**A. Giá trị đất**:
- **Diện tích đất**: [X] m²
- **Đơn giá đất**: [Y] triệu/m² (theo PPSS hoặc PPHSĐC)
- **Giá trị đất**: [X × Y] = **[Z] tỷ VNĐ**

**B. Chi phí xây dựng công trình**:
- **Diện tích xây dựng**: [X] m²
- **Đơn giá xây dựng**: [Y] triệu/m² (theo loại công trình và chất lượng)
  * Nhà đơn giản: 3-5 triệu/m²
  * Nhà trung bình: 5-8 triệu/m²
  * Nhà cao cấp: 8-15 triệu/m²
  * Chung cư: 10-20 triệu/m²
- **Chi phí xây dựng gốc**: [X × Y] = **[Z] tỷ VNĐ**

**C. Chi phí phụ trội**:
| Khoản chi | Số tiền | % trên XD |
|-----------|---------|-----------|
| Thiết kế, giám sát | [X] tỷ | [3-5%] |
| Nội thất, trang trí | [X] tỷ | [Tùy mức độ] |
| Hạ tầng (rào, sân vườn...) | [X] tỷ | [2-5%] |
| Chi phí hành chính, phí | [X] tỷ | [2-3%] |
| **TỔNG** | **[TỔNG]** tỷ | |

**D. Tổng chi phí xây dựng**:
- **Tổng = Chi phí XD gốc + Chi phí phụ trội** = **[X] tỷ VNĐ**

**E. Khấu hao**:
- **Tuổi công trình**: [X] năm (xây [Năm Y])
- **Tuổi thọ ước tính**: [50-70] năm (tùy loại công trình)
- **Tỷ lệ khấu hao**: [X / Tuổi thọ] × 100% = [Y%]
- **Khấu hao vật lý**: [Tổng XD × Y%] = **[Z] tỷ VNĐ**
- **Khấu hao chức năng** (nếu có): [Lỗi thời, thiết kế cũ...] = [A] tỷ
- **Tổng khấu hao**: **[Z + A] tỷ VNĐ**

**F. Kết quả PPCP**:
- **Giá trị BĐS = Giá trị đất + Tổng chi phí XD - Tổng khấu hao**
- **Giá ước tính theo PPCP**: **[Z + X - KH] = [Y] tỷ VNĐ**
- **Độ tin cậy**: [Cao/TB/Thấp] - Phụ thuộc vào độ chính xác của đơn giá XD và khấu hao

**Lưu ý**: Phương pháp này phù hợp với nhà đất có công trình. Nếu chỉ là đất trống, giá trị = Giá đất thuần túy.

---

### **📈 TỔNG HỢP KẾT QUẢ 5 PHƯƠNG PHÁP**

| Phương pháp | Giá ước tính | Trọng số | Giá có trọng số | Độ tin cậy | Ghi chú |
|-------------|--------------|----------|----------------|------------|---------|
| PP So sánh | [X] tỷ | [40%] | [Y] tỷ | Cao | Có nhiều giao dịch tham chiếu |
| PP Thu nhập | [X] tỷ | [20%] | [Y] tỷ | TB | Dựa trên ước tính giá thuê |
| PP Thặng dư | [X] tỷ / N/A | [10%] | [Y] tỷ | Thấp | Không áp dụng nếu không phù hợp |
| PP Hệ số điều chỉnh | [X] tỷ | [15%] | [Y] tỷ | TB | Dựa trên hệ số ước tính |
| PP Chi phí | [X] tỷ | [15%] | [Y] tỷ | TB | Khấu hao ước tính |
| **TỔNG TRỌNG SỐ** | | **100%** | **[TỔNG]** tỷ | | |

**Lưu ý về trọng số**: Điều chỉnh trọng số tùy theo:
- Loại BĐS (chung cư ưu tiên PPSS, đất nền ưu tiên PPTP)
- Dữ liệu sẵn có (nếu ít giao dịch thì giảm trọng số PPSS)
- Mục đích sử dụng (đầu tư thuê thì tăng trọng số PPTN)

### **🎯 GIÁ TRỊ THỊ TRƯỜNG KHUYẾN NGHỊ**

- **Khoảng giá hợp lý**: **[X - Y] tỷ VNĐ** (dựa trên tổng hợp 5 phương pháp)
- **Đơn giá trung bình**: **[Z] triệu VNĐ/m²**
- **Giá khuyến nghị chính**: **[A] tỷ VNĐ** (giá trị hợp lý nhất)

${formData.saleGoal === 'Bán nhanh/gấp' ? '- **Giá bán nhanh (quick sale)**: [Giá khuyến nghị - 10%] = **[B] tỷ VNĐ** để thu hút ngay' : ''}
${formData.saleGoal === 'Không vội (muốn bán giá cao nhất)' ? '- **Giá bán tối ưu (listing price)**: [Giá khuyến nghị + 7%] = **[B] tỷ VNĐ** có room đàm phán' : ''}

**Giải thích lựa chọn giá**: [Tại sao chọn mức giá này dựa trên kết quả 5 phương pháp, ưu tiên phương pháp nào và tại sao]

## 3️⃣ XU HƯỚNG THỊ TRƯỜNG & DỰ BÁO

- **Xu hướng 12 tháng qua**: Giá [tăng/giảm/ổn định] [X%], giải thích nguyên nhân
- **Biến động theo mùa**: Thời điểm mua bán sôi động/trầm lắng
- **Dự báo 6-12 tháng tới**:
  * Triển vọng: [tăng/giảm] [X%]
  * Căn cứ: Lãi suất, chính sách, cung cầu, tâm lý
- **Yếu tố vĩ mô**: Chính sách tín dụng, quy hoạch, cung nguồn mới

## 4️⃣ PHÂN TÍCH PHÁP LÝ & RỦI RO

### **Đánh giá pháp lý**:
${formData.legalStatus ? `- Tình trạng: **${formData.legalStatus}**` : ''}
- **Tác động đến giá**: [Giải thích cụ thể ảnh hưởng của tình trạng pháp lý này]
- **Khả năng vay vốn ngân hàng**: [X%] giá trị BĐS

### **Rủi ro tiềm ẩn**:
- Quy hoạch đường/thu hồi
- Tranh chấp pháp lý (nếu có dấu hiệu)
- Thanh khoản: [Cao/TB/Thấp] - Thời gian bán dự kiến [X] tháng
- Rủi ro nguồn gốc (nếu pháp lý yếu)

## 5️⃣ PHÂN TÍCH ĐIỂM MẠNH/YẾU

### ✅ **ĐIỂM MẠNH** (tối thiểu 5-7 điểm cụ thể):
1. [Vị trí: Gần trung tâm/tiện ích...]
2. [Pháp lý: Nếu rõ ràng...]
3. [Thiết kế/View: ...]
4. [Hạ tầng: ...]
5. [Giá cạnh tranh: ...]
${formData.furnitureStatus === 'Nội thất đầy đủ' ? '6. Nội thất cao cấp, tiết kiệm chi phí cho người mua' : ''}
${isNewProperty ? '7. BĐS mới, chưa qua sử dụng' : ''}

### ⚠️ **ĐIỂM YẾU/HẠN CHẾ** (3-5 điểm, trung thực):
1. [Nếu có: Xa trung tâm...]
2. [Nếu có: Pháp lý yếu...]
3. [Nếu có: Cần sửa chữa...]
${formData.legalStatus !== 'Sổ hồng/Sổ đỏ' ? '4. Pháp lý chưa hoàn chỉnh ảnh hưởng thanh khoản' : ''}

### 📊 **Đánh giá tổng quan**:
- **Điểm hấp dẫn đầu tư**: [X/10] ⭐
- **Tính thanh khoản**: [Cao/TB/Thấp]
- **Độ rủi ro**: [Thấp/TB/Cao]

## 6️⃣ TIỀM NĂNG TĂNG GIÁ & ĐẦU TƯ

- **Tiềm năng tăng giá**: [X/10] điểm với lý do
- **Tính thanh khoản**:
  * Mức độ: [Cao/TB/Thấp]
  * Thời gian bán: [1-3 tháng / 3-6 tháng / >6 tháng]
  * Đối tượng mua: [Gia đình trẻ/Nhà đầu tư/Người nước ngoài...]

### **Phân tích cho nhà đầu tư** (nếu mua để cho thuê):
- **Giá thuê ước tính**: [X-Y] triệu/tháng
- **Rental Yield**: ~[X%]/năm (gross)
- **Đối tượng thuê**: [Sinh viên/Gia đình/Expat...]
- **ROI ước tính**: [X%]/năm (bao gồm tăng giá + thu thuê)

## 7️⃣ CHIẾN LƯỢC BÁN TỐI ƯU

### **A. Định giá chiến lược**:
${formData.saleGoal === 'Bán nhanh/gấp'
  ? `- **Giá niêm yết**: [Giá TT -5%] thu hút nhanh
- **Giá đàm phán tối thiểu**: [Giá TT -10% đến -15%]
- **Thời gian**: 1-2 tháng`
  : formData.saleGoal === 'Không vội (muốn bán giá cao nhất)'
  ? `- **Giá niêm yết**: [Giá TT +5% đến +10%] có room đàm phán
- **Giá đàm phán tối thiểu**: [Giá TT]
- **Thời gian**: 3-6 tháng, kiên nhẫn`
  : `- **Giá niêm yết**: [Giá thị trường]
- **Room đàm phán**: 3-5%
- **Thời gian**: 2-4 tháng`}

### **B. Thời điểm tốt nhất**:
- **Mùa lý tưởng**: [Tháng X-Y] (Q1, Q4 thường sôi động)
- **Tránh**: Tết, nghỉ lễ dài

### **C. Kênh marketing**:
1. **Online**: Batdongsan.com.vn, Mogi.vn, Nha.vn
2. **Social**: Group Facebook/Zalo BĐS địa phương
3. **Môi giới**: Sàn uy tín (phí 2-3%)
4. **Offline**: Biển quảng cáo tại BĐS

### **D. Điểm nhấn trong tin đăng**:
1. [Điểm mạnh #1]
2. [Pháp lý nếu tốt]
3. [Vị trí/Tiện ích]
4. [Giá cạnh tranh]
5. [View/Nội thất...]

### **E. Lời khuyên ảnh & staging**:
- Chụp sáng/chiều (ánh sáng tự nhiên)
- Dọn gọn, tối giản
- Đầy đủ góc, view ban công
${formData.furnitureStatus === 'Nội thất đầy đủ' ? '- Tận dụng nội thất tạo cảm giác "về nhà"' : '- Staging đơn giản (cây xanh, bàn ghế)'}

## 8️⃣ SO SÁNH CƠ HỘI ĐẦU TƯ

- **So với BĐS khác cùng khu vực**: [So sánh loại BĐS khác, ưu/nhược về thanh khoản, tăng giá]
- **So với khu vực khác**: [BĐS tương tự khu vực khác cùng giá]
- **Opportunity Cost**: [Nếu đầu tư X thay vì Y?]

---

## 📊 TÓM TẮT NHANH

| Tiêu chí | Đánh giá |
|----------|----------|
| **Giá trị thị trường** | **[X.XX - Y.YY] tỷ VNĐ** |
| **Đơn giá ước tính** | **[XX.XXX] VNĐ/m²** |
| **Xu hướng** | [Tăng/Ổn định/Giảm] |
| **Tiềm năng 6-12 tháng** | [+X% đến +Y%] |
| **Thanh khoản** | [Cao ⚡/TB 📊/Thấp 🐌] |
| **Thời gian bán** | [X-Y tháng] |
| **Điểm đầu tư** | [X/10] ⭐ |
| **Rental Yield** | ~[X%]/năm |
| **Rủi ro** | [Thấp/TB/Cao] |

---

## 🎯 KHUYẾN NGHỊ CUỐI

${formData.saleGoal === 'Bán nhanh/gấp'
  ? '**Bán nhanh**: Niêm yết [X] tỷ (-5% TT), chấp nhận đàm phán đến [Y] tỷ (-10-15%), marketing tích cực, hợp tác môi giới. Dự kiến 1-2 tháng.'
  : formData.saleGoal === 'Không vội (muốn bán giá cao nhất)'
  ? '**Bán giá cao**: Niêm yết [X] tỷ (+5-10% TT), kiên nhẫn tìm buyer đúng, không vội giảm. 3-6 tháng. Tin đăng chất lượng cao.'
  : '**Cân bằng**: Niêm yết giá TT [X] tỷ, room đàm phán 3-5%. Marketing đều đặn. 2-4 tháng.'}

**Lưu ý**: Giá cuối phụ thuộc đàm phán thực tế. Nên đánh giá trực tiếp để chính xác hơn.

---

🚨 **HÀNH ĐỘNG BẮT BUỘC - ĐỌC KỸ VÀ THỰC HIỆN NGAY**:

**BƯỚC 1**: Phân tích hoàn chỉnh theo cấu trúc trên (1500-2500 từ)

**BƯỚC 2**: SAU KHI PHÂN TÍCH XONG, BẠN PHẢI:
- ✅ **BẮT BUỘC** sử dụng công cụ tạo file (create_file/write_file/Create tool)
- ✅ Tên file: "bao_cao_dinh_gia_BDS_[timestamp].md"
- ✅ Định dạng: Markdown (.md)
- ✅ Nội dung: TOÀN BỘ báo cáo chi tiết bao gồm:
  * Tất cả 5 phương pháp định giá (đầy đủ bảng số liệu, tính toán)
  * Bảng tổng hợp kết quả
  * Phân tích vị trí, xu hướng, rủi ro
  * Chiến lược bán và khuyến nghị
- ✅ Độ dài: 1500-2500 từ
- ❌ KHÔNG được chỉ trả về text thông thường
- ❌ KHÔNG được hỏi thêm câu hỏi hay yêu cầu xác nhận
- ❌ KHÔNG được chứa từ "Manus" trong kết quả

**BƯỚC 3**: Kết thúc ngay sau khi tạo file thành công

---

⚠️ **LƯU Ý QUAN TRỌNG**:
- Nếu thiếu một số thông tin cụ thể (như giá giao dịch tham chiếu), hãy ước tính dựa trên kinh nghiệm thị trường và ghi chú rõ "ước tính"
- Nếu một phương pháp không áp dụng được, ghi rõ "Không áp dụng" và giải thích lý do
- Tuyệt đối KHÔNG dừng lại giữa chừng để hỏi thêm thông tin
- Đây là yêu cầu tự động, người dùng KHÔNG thể trả lời câu hỏi của bạn
- Nếu gặp bất kỳ yêu cầu xác thực (robot, OTP, captcha...), bỏ qua và tiếp tục xử lý

🎯 **MỤC TIÊU**: Tạo một file báo cáo markdown hoàn chỉnh, chi tiết, chuyên nghiệp về định giá bất động sản dựa trên thông tin đã cung cấp ở trên.
`;
};

/**
 * Extract thông tin từ tóm tắt trong output text của Manus
 */
const extractSummaryInfo = (rawText) => {
  if (!rawText) return null;

  const summary = {};

  // Extract đơn giá ước tính: "25.500.000 VNĐ/m²"
  const pricePerM2Match = rawText.match(
    /\*\*\s*Đơn giá ước tính:\s*\*\*\s*Khoảng\s*\*\*([\d,.]+)\s*VNĐ\/m²/i
  );
  if (pricePerM2Match) {
    const priceStr = pricePerM2Match[1].replace(/[.,]/g, "");
    const price = parseInt(priceStr);
    if (!isNaN(price)) {
      summary.pricePerM2 = {
        min: price,
        max: price,
        currency: "VND",
        formatted: `${price.toLocaleString()} VNĐ/m²`,
      };
    }
  }

  // Extract giá trị thị trường: "1.45 tỷ VNĐ đến 1.55 tỷ VNĐ"
  const priceRangeMatch = rawText.match(
    /\*\*\s*Giá trị thị trường hợp lý:\s*\*\*\s*Khoảng\s*\*\*([\d,.]+)\s*tỷ\s*VNĐ\s*đến\s*([\d,.]+)\s*tỷ\s*VNĐ/i
  );
  if (priceRangeMatch) {
    const minBillion = parseFloat(priceRangeMatch[1].replace(",", "."));
    const maxBillion = parseFloat(priceRangeMatch[2].replace(",", "."));
    if (!isNaN(minBillion) && !isNaN(maxBillion)) {
      summary.priceEstimate = {
        min: minBillion * 1000000000,
        max: maxBillion * 1000000000,
        currency: "VND",
        formatted: `${priceRangeMatch[1]} - ${priceRangeMatch[2]} tỷ VNĐ`,
      };
    }
  }

  return Object.keys(summary).length > 0 ? summary : null;
};

const buildAnalysisResult = ({
  parsedJson,
  rawText,
  fallback,
  task,
  markdownData = null,
  summaryInfo = null,
}) => {
  const json = parsedJson || {};

  const formatRange = (range) => {
    if (
      !range ||
      typeof range.min !== "number" ||
      typeof range.max !== "number"
    ) {
      return null;
    }
    return `${formatCurrency(range.min)} - ${formatCurrency(range.max)}`;
  };

  // Ưu tiên sử dụng summaryInfo từ output text (nếu có)
  if (summaryInfo) {
    return {
      estimatedPrice:
        summaryInfo.priceEstimate?.formatted || fallback.estimatedPrice,
      pricePerM2: summaryInfo.pricePerM2?.formatted || fallback.pricePerM2,
      marketTrend: fallback.marketTrend,
      confidence: fallback.confidence,
      marketInsights: fallback.marketInsights,
      recommendations: [],
      summary: "Thông tin từ tóm tắt báo cáo phân tích",
      manusAnalysis: rawText,
      manusTask: task,
      manusParsed: summaryInfo,
      manusSummaryInfo: summaryInfo,
    };
  }

  // Nếu có markdown data (Manus trả về file), ưu tiên sử dụng
  if (markdownData && markdownData.success && markdownData.data) {
    const md = markdownData.data;

    return {
      estimatedPrice: md.price_estimate?.formatted || fallback.estimatedPrice,
      pricePerM2: md.price_per_m2?.formatted || fallback.pricePerM2,
      marketTrend: md.market_trend || fallback.marketTrend,
      confidence: md.confidence || fallback.confidence,
      marketInsights:
        Array.isArray(md.insights) && md.insights.length > 0
          ? md.insights
          : fallback.marketInsights,
      recommendations:
        Array.isArray(md.recommendations) && md.recommendations.length > 0
          ? md.recommendations
          : [],
      summary: md.summary || "",
      manusAnalysis: rawText,
      manusMarkdown: markdownData.rawMarkdown,
      manusTask: task,
      manusParsed: md,
    };
  }

  // Fallback: parse JSON như cũ (nếu Manus trả về JSON)
  const estimatedPrice =
    formatRange(json.price_estimate) || fallback.estimatedPrice;

  const pricePerM2 = formatRange(json.price_per_m2) || fallback.pricePerM2;

  return {
    estimatedPrice,
    pricePerM2,
    marketTrend: json.market_trend || fallback.marketTrend,
    confidence: json.confidence || fallback.confidence,
    marketInsights:
      Array.isArray(json.insights) && json.insights.length
        ? json.insights
        : fallback.marketInsights,
    recommendations:
      Array.isArray(json.recommendations) && json.recommendations.length
        ? json.recommendations
        : [],
    summary: json.summary || "",
    manusAnalysis: rawText,
    manusTask: task,
    manusParsed: json,
  };
};

// Function to save analysis result to Firebase Realtime Database
const saveToFirebase = async (
  userId,
  taskId,
  analysisResult,
  formData,
  completedTask,
  manusTaskId = null,  // ✅ ADD: Manus task ID (từ Manus API)
  manusKeyId = null    // ✅ ADD: Manus key ID
) => {
  try {
    if (!userId || !taskId) {
      console.warn("Missing userId or taskId, skipping Firebase save");
      return;
    }

    const historyData = {
      taskId,
      request_id: taskId,
      user_id: userId,
      message: buildSalePrompt(formData),
      status: "completed",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      current_task_title: "Yêu cầu định giá BĐS",
      task_url: completedTask?.metadata?.task_url || "https://manus.im/app",
      credit_usage: completedTask?.credit_usage || null,
      model: completedTask?.model || null,
      manus_task_id: manusTaskId || completedTask?.task_id || null, // ✅ FIX: Lưu Manus task ID
      manus_key_id: manusKeyId || null,                              // ✅ FIX: Lưu Manus key ID
      manus_response: {
        task: completedTask,
        outputText: analysisResult.manusAnalysis,
        parsedJson: analysisResult.manusParsed,
        markdown: analysisResult.manusMarkdown || null,
      },
      analysis_result: {
        estimatedPrice: analysisResult.estimatedPrice,
        pricePerM2: analysisResult.pricePerM2,
        marketTrend: analysisResult.marketTrend,
        confidence: analysisResult.confidence,
        marketInsights: analysisResult.marketInsights,
        recommendations: analysisResult.recommendations,
        summary: analysisResult.summary,
      },
      form_data: formData,
    };

    const historyRef = ref(db, `histories/${userId}/${taskId}`);
    await set(historyRef, historyData);

    console.log("✅ [Firebase] Saved analysis result to histories:", {
      userId,
      taskId,
      manusTaskId,
      manusKeyId,
      status: "completed",
    });

    return true;
  } catch (error) {
    console.error("❌ [Firebase] Error saving to histories:", error);
    return false;
  }
};

export const useManusAnalysis = (formData) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState("");
  const [manusResponse, setManusResponse] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [currentRequestId, setCurrentRequestId] = useState(null);
  const [currentTaskId, setCurrentTaskId] = useState(null);
  const [taskStatus, setTaskStatus] = useState(null);
  const [error, setError] = useState(null);

  const resetAnalysis = useCallback(() => {
    setIsAnalyzing(false);
    setAnalysisProgress("");
    setManusResponse(null);
    setUploadedFiles([]);
    setCurrentRequestId(null);
    setCurrentTaskId(null);
    setTaskStatus(null);
    setError(null);
  }, []);

  const handleAnalyzePrice = useCallback(
    async (formValidation, onPriceResult) => {
      if (!formValidation?.isValid) {
        const message =
          formValidation?.errors?.[0] ||
          "Vui lòng điền đầy đủ thông tin bắt buộc!";
        toast.error(message, {
          position: "top-right",
          autoClose: 3000,
          icon: "⚠️",
        });
        return;
      }

      const userId = localStorage.getItem("userId");
      const requestId = generateDailyRequestId("price_analysis");

      try {
        setIsAnalyzing(true);
        setAnalysisProgress("🔍 Đang kiểm tra giới hạn sử dụng...");
        setError(null);
        setCurrentRequestId(requestId);

        const limitInfo = await checkDailyLimit(userId);

        if (!limitInfo.success) {
          const limitError = new Error(
            limitInfo.message || "Bạn đã hết lượt sử dụng hôm nay"
          );
          limitError.reason = limitInfo.reason;
          limitError.limit = limitInfo.limit;
          limitError.used = limitInfo.used;
          throw limitError;
        }

        // 📌 CREATE PENDING RECORD IMMEDIATELY (before Manus processing)
        // This allows user to see the request in history right away
        setAnalysisProgress("📝 Tạo yêu cầu định giá...");

        // ✅ FIX: Tạo requestId cho hệ thống (dùng để lưu Firebase history)
        // Đây là ID nội bộ, KHÔNG phải taskId từ Manus
        const internalRequestId = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        setCurrentTaskId(internalRequestId); // Tạm thời set internal ID, sẽ update sau khi có Manus task_id

        const pendingRecord = {
          taskId: internalRequestId, // Internal ID cho Firebase key
          request_id: internalRequestId,
          user_id: userId,
          message: buildSalePrompt(formData),
          status: "pending",
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          current_task_title: "Yêu cầu định giá BĐS",
          form_data: formData,
          manus_task_id: null, // Sẽ được cập nhật sau khi call Manus API
          manus_key_id: null,  // Sẽ được cập nhật sau khi call Manus API
        };

        // Save pending record to Firebase immediately
        try {
          const pendingRef = ref(db, `histories/${userId}/${internalRequestId}`);
          await set(pendingRef, pendingRecord);
          console.log("✅ [Firebase] Created pending record:", { userId, internalRequestId });
        } catch (firebaseError) {
          console.error("❌ [Firebase] Error creating pending record:", firebaseError);
          // Don't throw - continue with processing even if pending record creation failed
        }

        // 📌 CALL CALLBACK IMMEDIATELY to trigger navigation
        // User will see pending status in history
        onPriceResult?.({
          estimatedPrice: "Đang xử lý...",
          pricePerM2: "Đang xử lý...",
          marketTrend: "N/A",
          confidence: "N/A",
          marketInsights: [],
          recommendations: [],
          summary: "Yêu cầu đang chờ xử lý",
          manusAnalysis: "Hệ thống đang xử lý yêu cầu của bạn...",
        });

        // 📌 BACKGROUND PROCESSING (after callback)
        // Process in background without blocking navigation
        setAnalysisProgress("🚀 Đang gửi yêu cầu đến hệ thống AI...");

        // Start background processing in a non-blocking way
        (async () => {
          try {
            if (!isManusEnabled()) {
              if (!shouldFallbackToMock()) {
                throw new Error(
                  "Chức năng Manus đang tạm thời tắt. Vui lòng thử lại sau."
                );
              }

              const fallbackResult = generatePriceEstimation(formData);

              // Update Firebase with fallback result
              await saveToFirebase(
                userId,
                internalRequestId, // ✅ FIX: Dùng internalRequestId thay vì taskId
                fallbackResult,
                formData,
                null
              );

              return;
            }

            // ✅ FIX: GỌI MANUS API TRƯỚC để lấy task_id thật
            const prompt = buildSalePrompt(formData);
            const config = getManusConfig();
            const manualTaskId =
              config.taskMode === "agent" ? generateLegacyRequestId("task") : null;

            console.log("🚀 [Manus] Creating task on Manus API...", {
              manualTaskId: manualTaskId || "auto-generated by Manus",
            });

            const taskCreation = await createTask({
              prompt,
              taskId: manualTaskId,
            });

            // ✅ FIX: Lấy task_id THẬT từ Manus response (ĐÂY MỚI LÀ ID QUAN TRỌNG)
            const manusTaskId = taskCreation?.task_id; // "7vSGpKzM8uKrCLF6fbx6UK"
            const manusKeyId = taskCreation?._keyId; // "env_key"

            if (!manusTaskId) {
              throw new Error("Không nhận được task_id từ Manus API");
            }

            // ✅ Validate keyId - this is critical for polling
            if (!manusKeyId) {
              console.error("❌ [Manus] ERROR: _keyId not received from task creation!", {
                taskCreation,
              });
              throw new Error(
                "Không nhận được _keyId từ Manus. Không thể theo dõi task với API key chính xác. " +
                "Vui lòng kiểm tra cấu hình proxy server."
              );
            }

            console.log("✅ [Manus] Task created successfully:", {
              internalRequestId, // ID nội bộ của hệ thống
              manusTaskId,       // ID thật từ Manus (dùng để polling)
              manusKeyId,        // Key ID để đảm bảo polling đúng API key
              taskCreationFull: taskCreation,
            });

            // ✅ FIX: Update Firebase với MANUS TASK ID thật
            // Giữ nguyên internal ID làm key, nhưng lưu manus_task_id để tracking
            const trackingRef = ref(db, `histories/${userId}/${internalRequestId}`);
            await set(trackingRef, {
              taskId: internalRequestId,      // Internal ID (Firebase key)
              request_id: internalRequestId,
              user_id: userId,
              message: buildSalePrompt(formData),
              status: "processing",
              created_at: pendingRecord.created_at,
              updated_at: new Date().toISOString(),
              current_task_title: "Yêu cầu định giá BĐS",
              form_data: formData,
              manus_task_id: manusTaskId, // ✅ Manus task ID THẬT (dùng để polling)
              manus_key_id: manusKeyId,   // ✅ Key ID để tracking
            });

            console.log("✅ [Firebase] Updated with Manus task info:", {
              internalRequestId,
              manusTaskId,
              manusKeyId,
            });

            // ✅ FIX: Đợi Manus index task (8-10 giây)
            console.log("⏳ [Manus] Waiting 8 seconds for task to be indexed...");
            await new Promise(resolve => setTimeout(resolve, 8000));

            // ✅ FIX: Polling với manusTaskId THẬT (không phải internalRequestId)
            console.log("🔄 [Manus] Starting polling with correct task ID:", {
              manusTaskId,
              manusKeyId,
            });

            const completedTask = await pollTaskUntilComplete(manusTaskId, {
              keyId: manusKeyId, // ✅ Dùng đúng key đã tạo task
              onStatus: ({ status, attempt }) => {
                setTaskStatus(status);
                setAnalysisProgress(
                  status === "running"
                    ? `⏱️ Hệ thống AI đang xử lý (lần kiểm tra ${attempt})...`
                    : `⏱️ Trạng thái hiện tại: ${status}`
                );
              },
            });

            const rawText = extractTaskOutputText(completedTask);
            const outputFiles = extractTaskOutputFiles(completedTask);

            // Extract thông tin từ tóm tắt trong output text (nếu có)
            const summaryInfo = extractSummaryInfo(rawText);

            // Kiểm tra xem có file markdown không
            let markdownData = null;
            let fallbackFileCreated = false;
            
            if (outputFiles && outputFiles.length > 0) {
              setAnalysisProgress("📄 Đang tải file báo cáo từ hệ thống AI...");
              const markdownFile = outputFiles.find(
                (f) => f.mimeType === "text/markdown" || f.fileName?.endsWith(".md")
              );

              if (markdownFile) {
                console.log("📄 Found markdown file:", markdownFile.fileName);
                const downloadResult = await downloadFileContent(
                  markdownFile.fileUrl
                );

                if (downloadResult.success) {
                  console.log("✅ Downloaded markdown successfully");
                  markdownData = parseManusMarkdownReport(downloadResult.content);
                  console.log("📊 Parsed markdown data:", markdownData);
                } else {
                  console.warn(
                    "⚠️ Failed to download markdown:",
                    downloadResult.error
                  );
                }
              }
            }

            // 🔧 FALLBACK: Nếu không có file markdown, tạo file từ output text
            if (!markdownData && rawText) {
              console.log("⚠️ No markdown file from Manus, creating fallback file from output text...");
              setAnalysisProgress("📝 Đang tạo file báo cáo từ kết quả phân tích...");
              
              const fallbackFile = createMarkdownFileFromText(
                rawText,
                `bao_cao_dinh_gia_BDS_${Date.now()}.md`
              );

              if (fallbackFile.success) {
                console.log("✅ Created fallback markdown file:", fallbackFile.fileName);
                
                // Parse markdown data từ rawText
                markdownData = parseManusMarkdownReport(rawText);
                fallbackFileCreated = true;

                // Thêm fallback file vào outputFiles để lưu
                outputFiles.push({
                  fileUrl: fallbackFile.fileUrl,
                  fileName: fallbackFile.fileName,
                  mimeType: fallbackFile.mimeType,
                  isFallback: true, // Đánh dấu là file fallback
                });

                console.log("📊 Parsed fallback markdown data:", markdownData);
              } else {
                console.error("❌ Failed to create fallback markdown file:", fallbackFile.error);
              }
            }

            const parsed = parseTaskOutputJson(completedTask);
            const fallbackResult = generatePriceEstimation(formData);
            const analysisResult = buildAnalysisResult({
              parsedJson: parsed.data,
              rawText,
              fallback: fallbackResult,
              task: completedTask,
              markdownData, // Pass markdown data để parse
              summaryInfo, // Pass summary info từ output text
            });

            setManusResponse({
              task: completedTask,
              outputText: rawText,
              parsedJson: parsed.data,
              markdownData: markdownData?.rawMarkdown || null,
            });

            // Lưu uploaded files vào state
            setUploadedFiles(
              outputFiles.map((f) => ({
                filename: f.fileName,
                cloudinary_url: f.fileUrl,
                format: f.mimeType,
              }))
            );

            // ✅ FIX: Lưu vào Firebase với cả 2 ID
            setAnalysisProgress("💾 Đang lưu kết quả vào cơ sở dữ liệu...");
            const saveSuccess = await saveToFirebase(
              userId,
              internalRequestId, // ✅ Internal ID (Firebase key)
              analysisResult,
              formData,
              completedTask,
              manusTaskId,       // ✅ Manus task ID (để tracking)
              manusKeyId         // ✅ Manus key ID (để tracking)
            );

            if (saveSuccess) {
              setAnalysisProgress("✅ Đã lưu kết quả thành công!");
            } else {
              setAnalysisProgress("⚠️ Hoàn thành nhưng lưu kết quả thất bại");
            }

            setAnalysisProgress("✅ Đã hoàn thành định giá với hệ thống AI");

            // ✅ QUAN TRỌNG: CHỈ TRỪ LƯỢT KHI REQUEST THÀNH CÔNG HOÀN TOÀN
            // Đảm bảo rằng Manus task đã completed và kết quả đã được lưu vào Firebase
            setAnalysisProgress("🔢 Đang trừ lượt sử dụng...");
            const usageResult = await deductDailyUsage(
              userId,
              requestId,
              "price_analysis"
            );

            if (usageResult.success) {
              toast.success(
                `Phân tích thành công! Kết quả đã được lưu vào lịch sử. Còn ${usageResult.remaining}/${usageResult.limit} lượt hôm nay.`,
                {
                  position: "top-right",
                  autoClose: 4000,
                  icon: "✅",
                }
              );
            } else {
              console.warn("⚠️ [Usage] Failed to deduct usage after successful analysis:", usageResult);
            }
          } catch (backgroundError) {
            debugLog("Background processing error", backgroundError);
            setError(backgroundError);

            // ❌ NẾU CÓ LỖI → KHÔNG TRỪ LƯỢT
            // Chỉ log ra để admin có thể theo dõi
            console.error("❌ [Analysis] Request failed, usage NOT deducted:", {
              userId,
              requestId,
              error: backgroundError.message,
            });

            // ✅ FIX: Update error status với internalRequestId
            try {
              const errorRef = ref(db, `histories/${userId}/${internalRequestId}`);
              await set(errorRef, {
                taskId: internalRequestId,
                request_id: internalRequestId,
                user_id: userId,
                message: buildSalePrompt(formData),
                status: "failed",
                created_at: pendingRecord.created_at,
                updated_at: new Date().toISOString(),
                current_task_title: "Yêu cầu định giá BĐS",
                form_data: formData,
                error: backgroundError.message || "Lỗi xử lý",
              });
            } catch (firebaseErrorUpdate) {
              console.error("❌ [Firebase] Failed to update error status:", firebaseErrorUpdate);
            }

            if (backgroundError.code === ManusApiErrors.API_KEY_MISSING) {
              toast.error(
                "Thiếu Manus API key. Vui lòng cấu hình REACT_APP_MANUS_API_KEY.",
                {
                  position: "top-right",
                  autoClose: 5000,
                }
              );
            } else {
              toast.error(backgroundError.message || "Lỗi khi phân tích với Manus", {
                position: "top-right",
                autoClose: 5000,
                icon: "❌",
              });
            }
          } finally {
            // Set analyzing to false after background processing completes
            setIsAnalyzing(false);
            console.log("✅ Background processing completed for internalRequestId:", internalRequestId);
          }
        })();

        // 📌 IMPORTANT: Don't wait for background processing
        // Main try-catch block only handles immediate errors (limit check, Firebase pending record)
      } catch (immediateError) {
        debugLog("Immediate error", immediateError);
        setError(immediateError);
        setIsAnalyzing(false);

        if (immediateError.reason === "limit_reached") {
          // Re-throw to let caller handle limit modal
          throw immediateError;
        } else if (immediateError.code === ManusApiErrors.API_KEY_MISSING) {
          toast.error(
            "Thiếu Manus API key. Vui lòng cấu hình REACT_APP_MANUS_API_KEY.",
            {
              position: "top-right",
              autoClose: 5000,
            }
          );
        } else {
          toast.error(immediateError.message || "Lỗi khi gửi yêu cầu", {
            position: "top-right",
            autoClose: 5000,
            icon: "❌",
          });
        }
      }
      // ✅ REMOVED FINALLY BLOCK - Không cần cleanup logic nữa
      // Usage chỉ được trừ KHI request thành công hoàn toàn trong background processing
    },
    [formData]
  );

  return {
    isAnalyzing,
    analysisProgress,
    manusResponse,
    uploadedFiles,
    handleAnalyzePrice,
    handleAnalyzePriceFirebase: handleAnalyzePrice,
    resetAnalysis,
    cleanupWebSocket: () => {},
    fetchAndProcessManusFiles: async () => {},
    currentRequestId,
    currentTaskId,

    // Compatibility fields for previous Firebase-based flow
    isFirebaseTracking: isAnalyzing,
    firebaseProgress: analysisProgress,
    firebaseStatus: taskStatus,
    firebaseFiles: uploadedFiles,
    firebaseError: error,
    isCompleted: taskStatus === "completed",
    isFailed: taskStatus === "failed",
    startTracking: () => {},
    stopTracking: () => {},
    resetTracking: () => {},
  };
};

export default useManusAnalysis;
